import { MatDialog } from '@angular/material/dialog';
import { Observable, firstValueFrom } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { AuthService } from 'src/app/services/auth.service';
import { CommonService } from 'src/app/services/common.service';
import { ToastService } from 'src/app/services/toast.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridSettingsService } from '../core-grid/grid-settings-flyout/grid-settings.service';
import { CoreGridComponent } from '../core-grid/core-grid.component';
import { GridModel } from '../grid.model';
import { ColumnDetails } from 'src/app/models/columnDetails.model';
import { getHistoryChangedFields } from 'src/app/_helpers/history-changed-fields.helper';

export abstract class HistoryGridComponent<T, TKey = number> extends CoreGridComponent<T> {

    protected entityId: TKey | null = null;

    constructor(
        toastService: ToastService,
        dialog: MatDialog,
        commonService: CommonService,
        authService: AuthService,
        gridViewService: GridViewService,
        gridSettingsService: GridSettingsService
    ) {
        super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
    }

    protected override get isGridViewEnabled(): boolean {
        return true;
    }

    override get hasOrgFilter(): boolean {
        return false;
    }

    protected setEntityId(entityId: TKey): void {
        this.entityId = entityId;
    }

    override loadPage(): void {
        if (this.entityId === undefined || this.entityId === null) {
            return;
        }

        this.isLoading = true;

        const params = this.buildHistoryParams();

        this.getHistoryRecords(params).subscribe({
            next: (result: GridModel<T>) => {
                this.dataSource.updateData(result.data);
                this.dataSource.setCount(result.count);
                this.isLoading = false;
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isLoading = false;
            }
        });
    }

    protected buildHistoryParams(): HttpParams {
        const filterBy = this.getFilterBy();
        const page = this.paginator ? this.paginator.pageIndex : 0;
        const pageSize = this.paginator ? this.paginator.pageSize : this.getPageSize();

        let params = new HttpParams()
            .set('filter', this.searchField?.nativeElement?.value || '')
            .set('orderBy', this.sortActive || '')
            .set('sort', this.defaultOrderBy || 'desc')
            .set('page', page.toString())
            .set('pageSize', pageSize.toString());

        if (Object.keys(filterBy).length > 0) {
            params = params.set('filterBy', JSON.stringify(filterBy));
        }

        return params;
    }

    override async getColumnDetailsAsync(): Promise<{ [property: string]: ColumnDetails }> {
        try {
            this.columnDetails = await firstValueFrom(this.getHistoryFieldConfiguration());
            return this.columnDetails;
        } catch {
            return {};
        }
    }

    protected abstract getHistoryRecords(params: HttpParams): Observable<GridModel<T>>;

    protected abstract getHistoryFieldConfiguration(): Observable<{ [property: string]: ColumnDetails }>;

    override initializeProperties(): void {
        this.addDisabled = true;
        this.editDisabled = true;
        this.deleteDisabled = true;
        this.defaultOrderBy = 'desc';
    }

    protected getChangedFieldsDisplay(changedFields: string | undefined, changedFieldsArray?: string[]): string {
        const fields = getHistoryChangedFields({ changedFields, changedFieldsArray });
        return fields.length ? fields.join(', ') : '-';
    }

    override openEntityDialog(entityId?: string): void {
        return;
    }

    override openEntityDeleteDialog(entity: T): void {
        return;
    }

    override initializeOverviewConfigs(): void {
        this.overviewConfigs = [];
    }

    override getOverviewData(): Observable<void> {
        return new Observable(observer => {
            observer.next();
            observer.complete();
        });
    }
}
